import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Tracking from '@spotahome/soyuz-tracking';

import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CookiesBanner from '../../components/CookiesBanner';
import AppBanner from '../../components/AppBanner';

import { loadModules, parseConfigurations } from '../../configurator';

import getSEOLandingQueries from '../../common/graphql/queries/seoLandings';
import pageMetadataBuilder from '../../common/metadata-builder';

import { SeoRoutingProvider } from '../../context/SeoRoutingContext';
import { SeoCitiesProvider } from '../../context/SeoCitiesContext';

const requestLandingConfig = async (slug, locale, graphqlClient) => {
  try {
    const response = await getSEOLandingQueries(graphqlClient)(slug, locale);

    return {
      seoLanding: response.data.seoLanding,
      cities: response.data.allCities
    };
  } catch (error) {
    console.log(error);
    return {};
  }
};

const buildHeaderData = (request, seoLanding, clientConfig) => {
  const dimensionManager = Tracking.DimensionManager.createInstance();
  const { locale } = request;
  const { seoMetaTitle, seoMetaDescription, cityId } = seoLanding;
  const innerHTMLs = [`ga('set', 'dimension4', '${locale}')`];
  const facebookMetadata = { appId: clientConfig.FB_APP_ID };
  const msMetadata = { validate: clientConfig.MSVALIDATE };
  const metas = pageMetadataBuilder({
    basic: {
      title: seoMetaTitle,
      description: seoMetaDescription,
      indexable: true
    },
    og: {
      title: seoMetaTitle,
      description: seoMetaDescription,
      locale
    },
    fb: facebookMetadata,
    ms: msMetadata
  });

  dimensionManager.setCity(cityId);

  return {
    title: `${seoMetaTitle} | Spotahome`,
    metas,
    scripts: [...innerHTMLs, ...dimensionManager.flushToScripts()].map(
      html => ({
        innerHTML: html
      })
    )
  };
};

const buildAppData = async (
  locale,
  seoLanding,
  cities,
  domain,
  graphqlClient
) => {
  const parsedConfiguration = await Promise.all(
    parseConfigurations(seoLanding.modules, locale, domain, graphqlClient)
  );

  return {
    modules: parsedConfiguration,
    routing: seoLanding.routing,
    currentCity: seoLanding.cityId,
    cities
  };
};

class SeoPage extends Component {
  static NAME = 'SeoPage';

  static contextTypes = {
    data: PropTypes.shape({
      [SeoPage.NAME]: PropTypes.object
    }).isRequired
  };

  static async requestAllData(
    request,
    domain,
    { clientConfig, res, graphqlClient }
  ) {
    const enabledCities = Object.keys(clientConfig.enabledCities).filter(
      key => clientConfig.enabledCities[key]
    );

    try {
      const { locale, params } = request;
      const { seoLanding, cities } = await requestLandingConfig(
        params.slug,
        locale,
        graphqlClient
      );

      if (seoLanding) {
        const headerData = buildHeaderData(request, seoLanding, clientConfig);
        const appData = await buildAppData(
          request.locale,
          seoLanding,
          cities,
          domain,
          graphqlClient
        );

        if (enabledCities.includes(appData.currentCity)) {
          return { headers: headerData, data: appData };
        }
      }

      res.redirect('/404');
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 404) {
        res.redirect('/404');
      }
    }

    return { headers: {}, data: {} };
  }

  state = {
    ...this.context.data[SeoPage.NAME]
  };

  render() {
    return (
      <SeoRoutingProvider value={this.state.routing}>
        <SeoCitiesProvider
          value={{
            cities: this.state.cities,
            currentCity: this.state.currentCity
          }}
        >
          <Header />
          {this.state.modules && loadModules(this.state.modules)}
          <Footer />
          <CookiesBanner />
          <AppBanner />
        </SeoCitiesProvider>
      </SeoRoutingProvider>
    );
  }
}

export default SeoPage;
